import { navigate } from 'gatsby';
import Img from 'gatsby-image';
// @ts-ignore (no types available for 'gatsby-storyblok-image')
import { getFluidGatsbyImage } from 'gatsby-storyblok-image';
import React from 'react';

import { IProductGroup, IProductOptions } from '../../../_shared/interfaces/product-select';
import { HeaderHomeStoryblok } from '../../../_shared/interfaces/storyblok';
import { IOrderContext, OrderContext } from '../../../context/order/order.context';
import { getAllProductOptions } from '../../../lib/article/article';
import { getMultilinkURL, isGlobalDataSource } from '../../../lib/helpers/helpers';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import { trackEvent } from '../../../lib/track/track';
import HsButton from '../../_common/hs-button/hs-button';
import * as HeaderHomeStylesDr from './header-home.dr.module.scss';
import * as HeaderHomeStylesHs from './header-home.hs.module.scss';

const HeaderHome = ({ blok, storyblokCommon }: { blok: HeaderHomeStoryblok; storyblokCommon: StoryblokCommon }) => {
    const HeaderHomeStyles = storyblokCommon.configuration.content.theme === 'dr' ? HeaderHomeStylesDr : HeaderHomeStylesHs;
    const { order, updateOrder } = React.useContext<IOrderContext>(OrderContext);
    const [images, setImages] = React.useState<any[]>([]);
    const [allManufacturers, setAllManufacturers] = React.useState<string[]>([]);
    const [currentManufacturer, setCurrentManufacturer] = React.useState<string | undefined>();
    const [currentProduct, setCurrentProduct] = React.useState<string | undefined>();
    const [productOptions, setProductOptions] = React.useState<IProductGroup[]>([]);
    const [allProductOptions, setAllProductOptions] = React.useState<IProductOptions | undefined>({});
    const [productSelectIsOpen, setProductSelectIsOpen] = React.useState<boolean>(false);

    const getAllManufacturers = () => {
        const manufacturers: string[] = [];
        storyblokCommon.workshopServices.content.manufacturer.forEach((entry) => {
            manufacturers.push(entry.name);
        });
        setAllManufacturers(manufacturers);
        if (manufacturers.length === 1) {
            setCurrentManufacturer(manufacturers[0]);
        } else {
            setCurrentManufacturer(order.manufacturer);
        }
    };

    const onSubmit = () => {
        if (currentManufacturer) {
            trackEvent('productselector.use', {
                product: { id: currentProduct, manufacturer: currentManufacturer },
            });

            updateOrder({
                manufacturer: currentManufacturer,
                articles: [{ product: currentProduct ? currentProduct : '', service: '', notes: '', amount: 1 }],
            });
        }
        navigate(getMultilinkURL(blok.ps_workshop_services_link));
    };

    React.useEffect(() => {
        const imageList = [];
        if (blok.background_mobile && blok.background_mobile.filename) {
            imageList.push({ ...getFluidGatsbyImage(blok.background_mobile.filename), media: '(max-width: 767px)' });
        }
        if (blok.background_desktop && blok.background_desktop.filename) {
            imageList.push({ ...getFluidGatsbyImage(blok.background_desktop.filename), media: '(min-width: 768px)' });
        }
        setImages(imageList);
    }, [blok]);

    React.useEffect(() => {
        if (allProductOptions && currentManufacturer) {
            setProductOptions(allProductOptions[currentManufacturer] ?? []);
            if (order.articles?.length) {
                setCurrentProduct(order.articles[0].product);
            }
        } else {
            setProductOptions([]);
        }
    }, [allProductOptions, currentManufacturer]);

    React.useEffect(() => {
        getAllManufacturers();
        setAllProductOptions(getAllProductOptions(storyblokCommon.workshopServices.content, storyblokCommon));
    }, []);

    React.useEffect(() => {
        if (productSelectIsOpen) {
            trackEvent('productselector.open', {});
        }
    }, [productSelectIsOpen]);

    return (
        <div className={HeaderHomeStyles.headerHome} style={{ backgroundColor: blok.background_color?.color ?? '' }}>
            {images.length && (
                <div className={HeaderHomeStyles.imageWrapper}>
                    <Img fluid={images} alt={blok.background_desktop?.alt ?? ''} />

                    {blok.background_cover !== 'none' && (
                        <div className={[HeaderHomeStyles.cover, HeaderHomeStyles[blok.background_cover ?? '']].join(' ')} />
                    )}
                </div>
            )}

            {blok.image_slope && <div className={HeaderHomeStyles.slope} />}

            <div className={[HeaderHomeStyles.inner, images.length ? HeaderHomeStyles.innerWithImage : ''].join(' ')}>
                <div
                    style={{
                        paddingTop: (blok.padding_top ?? '0') + '%',
                        paddingRight: (blok.padding_right ?? '0') + '%',
                        paddingBottom: (blok.padding_bottom ?? '0') + '%',
                        paddingLeft: (blok.padding_left ?? '0') + '%',
                    }}
                >
                    {blok.headline && <h1 style={{ color: blok.text_color }}>{blok.headline}</h1>}
                    {blok.subheadline && <h2 style={{ color: blok.text_color }}>{blok.subheadline}</h2>}

                    <div className={HeaderHomeStyles.productSelector}>
                        <div>
                            <label htmlFor="home-main-manufacturer">
                                <h4>{blok.ps_manufacturer_label}</h4>
                            </label>

                            {storyblokCommon.workshopServices.content.manufacturer.length > 1 ? (
                                <select
                                    id="home-main-manufacturer"
                                    value={currentManufacturer}
                                    onChange={(e) => {
                                        setCurrentManufacturer(e.target.value);
                                    }}
                                >
                                    <option value="">{storyblokCommon.configuration.content.select_default}</option>
                                    {storyblokCommon.workshopServices.content.manufacturer.map((manufacturer) => {
                                        if (isGlobalDataSource(storyblokCommon) && manufacturer.component.includes('global')) {
                                            return (
                                                <option key={manufacturer.name} value={manufacturer.name}>
                                                    {manufacturer.name}
                                                </option>
                                            );
                                        } else if (!isGlobalDataSource(storyblokCommon) && !manufacturer.component.includes('global')) {
                                            return (
                                                <option key={manufacturer.name} value={manufacturer.name}>
                                                    {manufacturer.name}
                                                </option>
                                            );
                                        }
                                    })}
                                </select>
                            ) : (
                                <p>{allManufacturers[0]}</p>
                            )}
                        </div>
                        <div className={!currentManufacturer ? HeaderHomeStyles.disabled : ''}>
                            <label htmlFor="home-main-product">
                                <h4>{blok.ps_product_label}</h4>
                            </label>
                            <select
                                id="home-main-product"
                                value={currentProduct}
                                onClick={() => setProductSelectIsOpen(!productSelectIsOpen)}
                                onChange={(e) => setCurrentProduct(e.target.value)}
                                disabled={!currentManufacturer}
                            >
                                <option value="">{storyblokCommon.configuration.content.select_default}</option>
                                {productOptions.map((group: any, groupIndex: number) => {
                                    return (
                                        <optgroup key={groupIndex} label={group.label}>
                                            {group.options.map((option: any, optionIndex: number) => {
                                                return (
                                                    <option key={optionIndex} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                );
                                            })}
                                        </optgroup>
                                    );
                                })}
                            </select>
                        </div>
                        <div>
                            <HsButton
                                appearance="primary"
                                size="large"
                                className={HeaderHomeStyles.psButton}
                                onClick={onSubmit}
                                storyblokCommon={storyblokCommon}
                            >
                                {blok.ps_submit_button}
                            </HsButton>
                        </div>
                    </div>

                    {blok.ps_powered_by_draeger && <div className={HeaderHomeStyles.poweredBy} />}

                    <HsButton
                        appearance="primary"
                        className={HeaderHomeStyles.mobileButton}
                        onClick={() => navigate(getMultilinkURL(blok.ps_workshop_services_link))}
                        storyblokCommon={storyblokCommon}
                    >
                        {blok.ps_submit_button_mobile}
                    </HsButton>
                </div>
            </div>
        </div>
    );
};

export default HeaderHome;
